import type {RefObject} from 'react';
import {useEffect, useRef} from 'react';

import type {CalculatorType} from './CalculatorTitleBar.react';

const TYPE_TRIGGER_SELECTOR = 'header [aria-haspopup="menu"]';

interface UseTypeSwitchFocusOptions {
  type: CalculatorType;
  isRendered: boolean;
  windowRef: RefObject<HTMLDivElement | null>;
}

/**
 * After a calculator type switch, moves focus to the new calculator's type
 * menu trigger, or to the window if it has none. Does nothing when focus was
 * outside the window, so a switch made from the page leaves focus where it is.
 *
 * Returns the ref to attach to the visible calculator's body, which is where
 * the new trigger is looked up.
 */
export const useTypeSwitchFocus = ({
  type,
  isRendered,
  windowRef
}: UseTypeSwitchFocusOptions): RefObject<HTMLDivElement | null> => {
  const visibleBodyRef = useRef<HTMLDivElement | null>(null);
  const isFocusLastInWindowRef = useRef(false);
  const previousTypeRef = useRef(type);

  // The menu restores focus to the trigger of the body that was just hidden.
  // Skipped when focus is on the page, so a type change from elsewhere stays put.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- imperative focus is a real side effect */
  useEffect(() => {
    if (previousTypeRef.current === type) {
      return;
    }
    previousTypeRef.current = type;
    const windowElement = windowRef.current;
    const active = document.activeElement;
    if (!isRendered || !windowElement) {
      return;
    }
    const focusIsInWindow =
      active && active !== document.body
        ? windowElement.contains(active)
        : isFocusLastInWindowRef.current;
    if (!focusIsInWindow) {
      return;
    }
    const trigger = visibleBodyRef.current?.querySelector<HTMLElement>(TYPE_TRIGGER_SELECTOR);
    (trigger ?? windowElement).focus();
  }, [type, isRendered, windowRef]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  // Hiding the old type's trigger can drop focus to body, so the type-change
  // effect above needs to know whether focus was last inside the window.
  useEffect(() => {
    if (!isRendered) {
      return;
    }
    const handleFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }
      isFocusLastInWindowRef.current = Boolean(windowRef.current?.contains(event.target));
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      isFocusLastInWindowRef.current = false;
    };
  }, [isRendered, windowRef]);

  return visibleBodyRef;
};
